/**
 * Cell Animation System
 * Handles membrane pulsing, organelle drift, and cytoplasm flow
 * Based on PRD Section 3.2 - Animation Requirements
 */

import Phaser from 'phaser';
import { SimplexNoise } from '@procedural/noise';
import { CellRenderer, type RenderedCellResult } from './CellRenderer';
import type { ProceduralCellConfig } from '@/types';

export interface AnimatedCell {
  result: RenderedCellResult;
  noise: SimplexNoise;
  time: number;
  organelleOffsets: OrganelleOffset[];
  pulsePhase: number;
  driftPhase: number;
}

interface OrganelleOffset {
  graphics: Phaser.GameObjects.Graphics;
  baseX: number;
  baseY: number;
  driftSpeed: number;
  driftRadius: number;
  phase: number;
}

export interface AnimationConfig {
  /** Membrane pulse amplitude (0-1) */
  pulseAmplitude: number;
  /** Membrane pulse speed (cycles per second) */
  pulseSpeed: number;
  /** Organelle drift radius in pixels */
  driftRadius: number;
  /** Organelle drift speed (0-1) */
  driftSpeed: number;
  /** Enable cytoplasm flow effect */
  cytoplasmFlow: boolean;
  /** Overall animation speed multiplier */
  speedMultiplier: number;
}

const DEFAULT_ANIMATION_CONFIG: AnimationConfig = {
  pulseAmplitude: 0.02,
  pulseSpeed: 0.5,
  driftRadius: 3,
  driftSpeed: 0.3,
  cytoplasmFlow: true,
  speedMultiplier: 1,
};

export class CellAnimationSystem {
  private cellRenderer: CellRenderer;
  private animatedCells: Map<string, AnimatedCell> = new Map();
  private animationConfig: AnimationConfig;
  private isRunning: boolean = false;

  constructor(scene: Phaser.Scene, config: Partial<AnimationConfig> = {}) {
    this.cellRenderer = new CellRenderer(scene);
    this.animationConfig = { ...DEFAULT_ANIMATION_CONFIG, ...config };
  }

  /**
   * Create an animated cell and register it for updates
   */
  createAnimatedCell(
    config: ProceduralCellConfig,
    seed: number = Date.now(),
    id?: string
  ): AnimatedCell {
    const cellId = id ?? `cell_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const result = this.cellRenderer.renderCell(config, seed);
    const noise = new SimplexNoise(seed);

    // Track organelle positions for drift animation
    const organelleOffsets: OrganelleOffset[] = result.organelles.map((graphics, index) => ({
      graphics,
      baseX: graphics.x,
      baseY: graphics.y,
      driftSpeed: 0.5 + noise.noise2DNormalized(index, 0) * 0.5,
      driftRadius: this.animationConfig.driftRadius * (0.5 + noise.noise2DNormalized(index, 1) * 0.5),
      phase: noise.noise2D(index, 2) * Math.PI * 2,
    }));

    const animatedCell: AnimatedCell = {
      result,
      noise,
      time: 0,
      organelleOffsets,
      pulsePhase: noise.noise2D(seed, 0) * Math.PI * 2,
      driftPhase: noise.noise2D(seed, 1) * Math.PI * 2,
    };

    this.animatedCells.set(cellId, animatedCell);

    return animatedCell;
  }

  /**
   * Remove an animated cell
   */
  removeAnimatedCell(id: string): void {
    const cell = this.animatedCells.get(id);
    if (cell) {
      cell.result.container.destroy();
      this.animatedCells.delete(id);
    }
  }

  /**
   * Update all animated cells (call in scene update)
   */
  update(delta: number): void {
    if (!this.isRunning) return;

    const deltaSeconds = (delta / 1000) * this.animationConfig.speedMultiplier;

    this.animatedCells.forEach((cell) => {
      cell.time += deltaSeconds;
      this.updateCell(cell, deltaSeconds);
    });
  }

  /**
   * Update a single animated cell
   */
  private updateCell(cell: AnimatedCell, _deltaSeconds: number): void {
    // Update membrane pulse
    this.updateMembranePulse(cell);

    // Update organelle drift
    this.updateOrganelleDrift(cell);

    // Update cytoplasm (if enabled)
    if (this.animationConfig.cytoplasmFlow) {
      this.updateCytoplasmFlow(cell);
    }

    // Update effects glow
    this.updateEffectsGlow(cell);
  }

  /**
   * Animate membrane with subtle pulsing/morphing
   */
  private updateMembranePulse(cell: AnimatedCell): void {
    const { membrane, config } = cell.result;
    const { pulseAmplitude, pulseSpeed } = this.animationConfig;

    // Calculate pulse factor
    const pulseFactor = 1 + Math.sin(cell.time * pulseSpeed * Math.PI * 2 + cell.pulsePhase) * pulseAmplitude;

    // Update membrane using morphing shape
    this.cellRenderer.updateMembrane(
      membrane,
      {
        ...config,
        membrane: {
          ...config.membrane,
          baseRadius: config.membrane.baseRadius * pulseFactor,
        },
      },
      cell.noise,
      cell.time * 1000
    );
  }

  /**
   * Animate organelles with subtle drift movement
   */
  private updateOrganelleDrift(cell: AnimatedCell): void {
    const { driftSpeed } = this.animationConfig;

    cell.organelleOffsets.forEach((offset) => {
      const t = cell.time * offset.driftSpeed * driftSpeed;

      // Use noise for organic movement
      const noiseX = cell.noise.noise2D(t, offset.phase);
      const noiseY = cell.noise.noise2D(t + 100, offset.phase);

      offset.graphics.x = offset.baseX + noiseX * offset.driftRadius;
      offset.graphics.y = offset.baseY + noiseY * offset.driftRadius;

      // Subtle rotation for some organelles
      offset.graphics.rotation = cell.noise.noise2D(t * 0.5, offset.phase + 50) * 0.1;
    });
  }

  /**
   * Animate cytoplasm with subtle flow effect
   */
  private updateCytoplasmFlow(cell: AnimatedCell): void {
    const { cytoplasm } = cell.result;

    // Subtle alpha modulation for flow effect
    const flowAlpha = 0.35 + Math.sin(cell.time * 0.3 + cell.driftPhase) * 0.05;
    cytoplasm.alpha = flowAlpha;
  }

  /**
   * Animate glow effects
   */
  private updateEffectsGlow(cell: AnimatedCell): void {
    const { effects } = cell.result;

    // Subtle glow pulse
    const glowAlpha = 0.8 + Math.sin(cell.time * 0.5 + cell.pulsePhase) * 0.2;
    effects.alpha = glowAlpha;
  }

  /**
   * Start animations
   */
  start(): void {
    this.isRunning = true;
  }

  /**
   * Stop animations
   */
  stop(): void {
    this.isRunning = false;
  }

  /**
   * Pause animations (alias for stop)
   */
  pause(): void {
    this.stop();
  }

  /**
   * Resume animations (alias for start)
   */
  resume(): void {
    this.start();
  }

  /**
   * Check if animations are running
   */
  get running(): boolean {
    return this.isRunning;
  }

  /**
   * Get animation configuration
   */
  getConfig(): AnimationConfig {
    return { ...this.animationConfig };
  }

  /**
   * Update animation configuration
   */
  setConfig(config: Partial<AnimationConfig>): void {
    this.animationConfig = { ...this.animationConfig, ...config };
  }

  /**
   * Get a specific animated cell by ID
   */
  getCell(id: string): AnimatedCell | undefined {
    return this.animatedCells.get(id);
  }

  /**
   * Get all animated cells
   */
  getAllCells(): Map<string, AnimatedCell> {
    return new Map(this.animatedCells);
  }

  /**
   * Get the number of animated cells
   */
  get cellCount(): number {
    return this.animatedCells.size;
  }

  /**
   * Position a cell's container
   */
  positionCell(id: string, x: number, y: number): void {
    const cell = this.animatedCells.get(id);
    if (cell) {
      cell.result.container.setPosition(x, y);
    }
  }

  /**
   * Scale a cell's container
   */
  scaleCell(id: string, scale: number): void {
    const cell = this.animatedCells.get(id);
    if (cell) {
      cell.result.container.setScale(scale);
    }
  }

  /**
   * Set cell visibility
   */
  setCellVisible(id: string, visible: boolean): void {
    const cell = this.animatedCells.get(id);
    if (cell) {
      cell.result.container.setVisible(visible);
    }
  }

  /**
   * Destroy all animated cells and clean up
   */
  destroy(): void {
    this.stop();
    this.animatedCells.forEach((cell) => {
      cell.result.container.destroy();
    });
    this.animatedCells.clear();
  }
}
